import DashboardIcon from '@mui/icons-material/Web';
import { flatten } from 'lodash';

import ELEMENT from 'common/element';
import * as K from 'common/constants';
import { UrlParams } from 'common/types';
import { buildUrl } from 'common/util/location';

import Link from 'components/common/Link/Link';
import Button from 'components/common/Button/Button';
import Table from 'components/common/Table/Table';
import ElementWithTooltip from 'components/common/ElementWithTooltip/ElementWithTooltip';
import { DashboardConfigType, DashboardGraphType, DashboardParam, GraphConfigType } from 'components/dashboards/types';
import { computeHref, getGraphTitle } from 'components/dashboards/dashboardData';

import './DashboardList.scss';

export type AllDashboardsParams = {
  configs: Array<DashboardConfigType>;
  filterText: string;
};

export const hasFilterMatches = (text: string, filterText: string): boolean => {
  if (filterText.length === 0) {
    return true;
  }
  const lcText = text.toLowerCase();
  const lcFilterText = filterText.toLowerCase();
  return lcText.includes(lcFilterText);
};

export const highlightFilterMatches = (text: string, filterText: string): JSX.Element => {
  if (filterText.length) {
    const lcText = text.toLowerCase();
    const lcFilterText = filterText.toLowerCase();
    if (lcText.includes(lcFilterText)) {
      const index = lcText.indexOf(lcFilterText);
      return (
        <>
          {text.substring(0, index)}
          <mark>
            {text.substr(index, filterText.length)}
          </mark>
          {text.substring(index + filterText.length, text.length)}
        </>
      );
    }
  }
  return <>{text}</>;
};

const generateGraphChiclet = (
  config: DashboardConfigType & { params?: Array<DashboardParam> },
  graph: DashboardGraphType,
  graphKey: string,
  filterText: string,
  graphIndex: number,
  tabName?: string,
): JSX.Element => {
  const dashboardName = config.name;
  const graphTitle = getGraphTitle(graph);
  const graphLink = computeHref(dashboardName, graph, graphIndex, config.params || [], tabName);
  if (graphLink.length > 0) {
    return (
      <Button className="DashboardList__Chiclet" key={graphKey} testId={ELEMENT.dashboardsListChiclet}>
        <ElementWithTooltip
          delay={{ show: 750, hide: 250 }}
          placement="bottom"
          tooltip="View as graph"
        >
          <a href={graphLink}>{highlightFilterMatches(graphTitle, filterText || '')}</a>
        </ElementWithTooltip>
      </Button>
    );
  }

  // If the panel is a table with 'columns' instead of 'query', then it shouldn't contain an href. However, we still want to show it in the dashboard list and search for it.
  if ((graph as GraphConfigType)?.columns) {
    return (
      <Button disabled className="DashboardList__Chiclet" key={graphKey} testId={ELEMENT.dashboardsListChiclet}>
        <div>{highlightFilterMatches(graphTitle, filterText || '')}</div>
      </Button>
    );
  }

  return <div key={graphKey} />; // The plan is to not support reports in the React app
};

/**
 * This component displays a list of all the dashboards. It does not support infinite scrolling.
 *
 * @prop configs    Array of all dashboards configurations (resolved)
 * @prop filterText Any text entered in the menu input field to filter the list of dashboards shown
 */
function DashboardList({
  configs,
  filterText,
}: AllDashboardsParams): JSX.Element {

  // No soup for you!
  if (configs?.length === 0) {
    return (<div className="DashboardList__NoMatches">No matching dashboards found.</div>);
  }

  const tableColumns = [
    { content: '', field: 'icon', className: 'DashboardList__Icon' },
    { content: 'Dashboard', field: 'name', className: 'DashboardList__ColName' },
    { content: 'Content', field: 'graphs', className: 'DashboardList__ColChiclets' },
    { content: '', field: 'more', className: 'DashboardList__ColMore' },
  ];

  const rows = configs.map((config, rowIndex) => {
    const dashboardLink = buildUrl(K.ROUTE.DASHBOARD, { [K.URL_PARAM.DASHBOARD_NAME]: config.name } as UrlParams);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rowData: { [key: string]: any } = {};
    if (config.tabs) {
      rowData.icon = <DashboardIcon />;
      rowData.name = (
      <>
        <div className="DashboardList__Name"><Link href={dashboardLink} testId={ELEMENT.dashboardsListName}>{highlightFilterMatches(config.name, filterText || '')}</Link></div>
        <div className="DashboardList__Description">{config.description}</div>
      </>
      );
      const graphs: { [key: string]: any } = {};
      for (const tab of config.tabs) {
        graphs[tab.tabName] = tab.graphs as DashboardGraphType[];
      }
      const graphsList = flatten(Object.values(graphs));
      const graphKeys = graphsList.map((_graph, indexGraph) => `dashboard-${rowIndex}-${indexGraph}`);
      rowData.graphs = graphsList.length > 0
        ? (
        <>
          {Object.keys(graphs).map((tabName) => graphs[tabName].map((graph: any, indexGraphName: any) => generateGraphChiclet(config, graph, graphKeys[indexGraphName], filterText, indexGraphName, tabName)))}
        </>
        )
        : <></>;
    } else {
      const graphs: DashboardGraphType[] = config.graphs as DashboardGraphType[];

      rowData.icon = <DashboardIcon />;
      rowData.name = (
      <>
        <div className="DashboardList__Name"><Link href={dashboardLink} testId={ELEMENT.dashboardsListName}>{highlightFilterMatches(config.name, filterText || '')}</Link></div>
        <div className="DashboardList__Description">{config.description}</div>
      </>
      );

      const graphKeys = graphs.map((_graph, indexGraph) => `dashboard-${rowIndex}-${indexGraph}`);
      rowData.graphs = graphs.length > 0
        ? (
        <>
          {graphs.map((graph, indexGraphName) => generateGraphChiclet(config, graph, graphKeys[indexGraphName], filterText, indexGraphName))}
        </>
        )
        : <></>;
    }
    
    return rowData;
  });

  const tableDisplayOptions = {
    hoverRow: false,
    sidePadding: 10,
    size: K.SIZE.LARGE,
    isStriped: false,
    isSortable: false,
    rowClass: 'DashboardList__TableRow',
    itemClass: 'DashboardList__TableItem',
    headerItemClass: 'DashboardList__TableHeaderItem',
  };

  return (
    <div className="DashboardList">
      <Table headers={tableColumns} data={rows} options={tableDisplayOptions} testId={ELEMENT.dashboardsList} />
    </div>
  );
}

export default DashboardList;
